import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { CriteriaWithLabel } from './types';
import { CriteriaBuilder, type CriteriaCondition } from '../CriteriaBuilder';

interface Grid3DAxisConfigProps {
  axis: 'x' | 'y' | 'z';
  axisLabel: string;
  entityType: string;
  criteria: CriteriaWithLabel[];
  onChange: (criteria: CriteriaWithLabel[]) => void;
  disabled?: boolean;
}

const AXIS_COLORS = {
  x: '#e3f2fd', // blue
  y: '#f3e5f5', // purple
  z: '#e8f5e9', // green
};

const AXIS_DESCRIPTIONS = {
  x: 'Row criteria (displayed on left side of grid)',
  y: 'Column criteria (displayed on top of grid)',
  z: 'Layer criteria (controls which layer/page of the 3D grid)',
};

export const Grid3DAxisConfig = ({
  axis,
  axisLabel,
  entityType,
  criteria,
  onChange,
  disabled = false,
}: Grid3DAxisConfigProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | false>(0);

  const handleLabelChange = (index: number, label: string) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], label };
    onChange(updated);
  };

  const handleConditionsChange = (index: number, conditions: CriteriaCondition[]) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], conditions };
    onChange(updated);
  };

  const handleDslChange = (index: number, dsl: string) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], dsl };
    onChange(updated);
  };

  return (
    <Card sx={{ backgroundColor: AXIS_COLORS[axis], mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {axisLabel} Axis
          </Typography>
          <Chip
            label={`${criteria.length} criteria`}
            size="small"
            color={criteria.every(c => c.conditions.length > 0) ? 'success' : 'default'}
          />
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {AXIS_DESCRIPTIONS[axis]}
        </Typography>

        {criteria.map((criterion, index) => (
          <Accordion
            key={index}
            expanded={expandedIndex === index}
            onChange={(_, isExpanded) => setExpandedIndex(isExpanded ? index : false)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Chip
                  label={`${axis.toUpperCase()}${index + 1}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography sx={{ flex: 1 }}>
                  {criterion.label || `Criterion ${index + 1}`}
                </Typography>
                {criterion.conditions.length > 0 && (
                  <Chip
                    label={`${criterion.conditions.length} condition${criterion.conditions.length > 1 ? 's' : ''}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="Label"
                value={criterion.label}
                onChange={(e) => handleLabelChange(index, e.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
                sx={{ mb: 2 }}
                helperText="Display label for this criterion (shown in game UI)"
              />
              <CriteriaBuilder
                entityType={entityType}
                value={criterion.conditions}
                onChange={(conditions) => handleConditionsChange(index, conditions)}
                onDslChange={(dsl) => handleDslChange(index, dsl)}
                disabled={disabled}
                label="Conditions"
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};
